import { GridCustomCellProps } from "@progress/kendo-react-grid";
import { ChargeType, PaymentStatus } from "../../../data/entities";
import { Icon, IconDefinitions } from "../../../components";


const hexToRgba = (hex: string, alpha: number) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const softBadgeStyle = (color: string, textColor?: string) => ({
  backgroundColor: hexToRgba(color, 0.1),
  color: textColor ?? color,
  borderColor: hexToRgba(color, 0.35),
});

const SoftBadge = ({ color, textColor, children }: { color: string; textColor?: string; children: string }) => (
  <span className="badge border" style={softBadgeStyle(color, textColor)}>{children}</span>
);

export const CreatedDateCell = (props: GridCustomCellProps) => {
  const unformattedDate = props.dataItem.createdDate;
  if (!unformattedDate) return <td>—</td>; 

  const date = new Date(unformattedDate); 
  if (isNaN(date.getTime())) return <td>Invalid</td>;

  return <td {...props.tdProps}>{date.toLocaleDateString()}</td>;
};

export const CustomerNameCell = (props: GridCustomCellProps) => {
  const customer = props.dataItem.invoice?.customer;
  const name = customer
    ? `${customer.firstName ?? ""} ${customer.lastName ?? ""}`.trim()
    : "";
  return <td {...props.tdProps}>{name}</td>;
};

export const BookingIdCell = (props: GridCustomCellProps) => {
  const booking = props.dataItem.invoice?.bookings[0];
  return <td {...props.tdProps}>{booking ? booking.id : "N/A"}</td>;
};

export const BookingCancelledCell = (props: GridCustomCellProps) => {
  const booking = props.dataItem.invoice?.bookings?.[0];
  const isCancelled = booking?.isCancelled;
  return (
    <td {...props.tdProps}>
      {isCancelled
        ? <SoftBadge color="#bc1945">Cancelled</SoftBadge>
        : <SoftBadge color="#28a745">Active</SoftBadge>}
    </td>
  );
};

// Shared between the payments grid and the payment details modal so the
// status always renders identically
export const PaymentStatusBadge = ({ status }: { status: PaymentStatus | string }) => {
    let displayStatus = String(status);
    if(status === PaymentStatus.PreAuthorized) displayStatus = "Pre-Authorized";

    let color = "#212529";
    // The light yellow/orange tints need darker text to stay readable,
    // mirroring how the appointments badge treats "warning"
    let textColor: string | undefined;
    switch (status) {
      case PaymentStatus.Pending:
        color = "#FFA500";
        textColor = "#997404";
        break;
      case PaymentStatus.PreAuthorized:
        color = "#007bff";
        break;
      case PaymentStatus.Captured:
      case PaymentStatus.Posted:
        color = "#28a745";
        break;
      case PaymentStatus.Refunded:
        color = "#343a40";
        break;
      case PaymentStatus.Voided:
      case PaymentStatus.Cancelled:
        color = "#6c757d";
        break;
      case PaymentStatus.Failed:
        color = "#bc1945";
        break;
      case PaymentStatus.Partial:
        color = "#ffc107";
        textColor = "#997404";
        break;
    }

    return <SoftBadge color={color} textColor={textColor}>{displayStatus}</SoftBadge>;
};

export const PaymentStatusCell = (props: GridCustomCellProps) => (
    <td {...props.tdProps}>
      <PaymentStatusBadge status={props.dataItem.status} />
    </td>
  );


export const CapturePreAuthCell = (showEdit: (dataItem: any) => void) =>
  (props: GridCustomCellProps) => {
    const status = props.dataItem.status;
    return (
      <td  {...props.tdProps} className="text-center action-col-td" >
        {status === PaymentStatus.PreAuthorized ? (
          <span className="d-flex justify-content-center">
            <button
                className="btn btn-success btn-sm w-100 w-md-auto d-flex align-items-center justify-content-center mb-2 mb-md-0 "
                 onClick={() => showEdit(props.dataItem)}
              >
                <Icon iconName={IconDefinitions.check} className="me-1 d-none d-lg-inline" />
                Capture Hold
              </button>
          </span>
        ) : null}
      </td>
    );
  }

export const VoidPreAuthCell = (showDelete: (dataItem: any) => void) =>
  (props: GridCustomCellProps) => {
    const status = props.dataItem.status;
    return (
      <td className="text-center action-col-td">
        {status === PaymentStatus.PreAuthorized ? (
          <span className="d-flex justify-content-center">
            <button
              className="btn btn-danger btn-sm w-100 w-md-auto d-flex align-items-center justify-content-center mb-2 mb-md-0"
              onClick={() => showDelete(props.dataItem)}
            >
              <Icon iconName={IconDefinitions.cancel} className="me-1 d-none d-lg-inline" />
              Void Hold
            </button>
          </span>
        ) : null}
      </td>
    );
  };


